/**
 * Chat v2 in-app notification middleware.
 *
 * Mirrors the mechanism legacy chat used to render an in-app toast on an
 * incoming OneSignal push (legacy/middleware.js + legacy/sagas.js notifyWithToast):
 * a Redux middleware captures the store, lazily registers a `notificationDisplay`
 * OneSignal listener after org features load, and the listener renders the toast
 * imperatively via react-toastify's `toast(...)`. The app-wide
 * DraggableToastContainer (mounted in pages/index.tsx) owns stacking, dismissal
 * and drag — there is no bespoke host/queue.
 *
 * Differences from v1:
 * - Short-circuits UNLESS enable_chat_v2 (v1 short-circuits when it IS true).
 * - Only listens for notificationDisplay + shows a toast. It does NOT manage the
 *   Sendbird connection and does NOT call setSubscription — the OneSignal SDK
 *   lifecycle (init/subscribe) is owned by components/OneSignalInit.
 */
import React from 'react';
import { toast } from 'react-toastify';
import { isMobile } from 'lib/systemHelpers/browserHelpers';
import { getOrganizationFeatures } from 'domain/organization/organizationActions';
import { signOutAction } from 'domain/env/envActions';
import { userGUIDSelector } from 'domain/env';
import { isNewChatSelector, activeChannelSelector } from '../chatSelector';
import { adaptOneSignalChatPush } from '../schemas';
import ChatToast, { ChatToastClose } from 'components/mui/Layouts/components/Chat/components/ChatToast';

const LOG = '[ChatV2Toast]';

function registerPushObserver(observer) {
  if (observer.attached) return;
  observer.attached = true;

  window.OneSignal = window.OneSignal || [];
  const { OneSignal } = window;

  OneSignal.push(() => {
    OneSignal.on('notificationDisplay', (event) => {
      observer.onNotificationDisplay(event);
    });
  });
}

function makeObserver(store) {
  // Dedup redelivered pushes (OneSignal can deliver the same push more than
  // once, and the OS push + foreground event can overlap). A per-session key
  // set is enough — react-toastify owns the visible toast lifecycle.
  const seenKeys = new Set();

  return {
    attached: false,
    // Cleared on sign-out so keys from a previous session can't suppress toasts
    // for the next signed-in user (the SPA / Redux store is not recreated).
    clearSeenKeys() {
      seenKeys.clear();
    },
    onNotificationDisplay(event) {
      // The listener is attached once, but the enable_chat_v2 flag can flip
      // within an SPA session (org switch / feature refresh). Re-check the
      // CURRENT flag on every push so legacy-mode users never get v2 toasts
      // from a previously-attached observer.
      if (!isNewChatSelector(store.getState())) return;

      // Mobile never shows the in-app toast (matches v1 notifyWithToast gating).
      if (isMobile) return;

      const entry = adaptOneSignalChatPush(event);
      // Not a chat push (or unparseable) — ignore.
      if (!entry) return;

      // Dedup by the stable key from the parsed push.
      if (entry.key && seenKeys.has(entry.key)) return;

      const state = store.getState();

      // Suppress the toast for the conversation the user is currently viewing.
      if (entry.channelUrl && entry.channelUrl === activeChannelSelector(state)) return;

      // Suppress the user's own messages. In chat v2, Sendbird is configured
      // with userProfile.id (= userGUID), and Sendbird Member.userId — which the
      // OneSignal push sender.user_id mirrors — is that userGUID, NOT the login
      // email. So compare against userGUIDSelector, not userIdSelector. Reuse the
      // already-parsed entry rather than re-parsing the event.
      const senderGUID = entry.senderUserGUID;
      if (senderGUID && senderGUID === userGUIDSelector(state)) return;

      if (entry.key) seenKeys.add(entry.key);

      toast(React.createElement(ChatToast, { toast: entry }), {
        type: toast.TYPE.INFO,
        closeButton: React.createElement(ChatToastClose),
      });
    },
  };
}

export default function chatV2ToastMiddleware() {
  return (store) => {
    // Defer init: org features aren't loaded at store-creation time, so the
    // enable_chat_v2 flag isn't readable yet. Attach the OneSignal listener the
    // first time features load AND v2 is enabled. If v2 is disabled at that
    // point we do NOT lock initialization — a later features refresh (re-auth /
    // org switch that turns v2 on) can still attach the observer. The observer
    // itself re-checks the flag per push, so it stays correct if v2 flips off.
    let observer = null;

    function lazyInit() {
      if (observer) return; // already attached this session
      if (!isNewChatSelector(store.getState())) return; // v2 off for now — retry on next features load

      observer = makeObserver(store);
      registerPushObserver(observer);
      console.log(LOG, 'notificationDisplay observer attached');
    }

    return (next) => (action) => {
      const result = next(action);

      if (action.type === getOrganizationFeatures.success) {
        lazyInit();
      } else if (action.type === signOutAction.success && observer) {
        observer.clearSeenKeys();
      }

      return result;
    };
  };
}
